import { cloneDeep, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { slices, useAppSelector } from 'src/redux';
import { PermissionApiName } from 'src/types';

const { selectors: userSelector } = slices.user;

export const usePermissions = () => {
  const userInfo = useAppSelector(userSelector.selectUserInfo);

  // Will return boolean if permission name exist in logged in user.
  const permissionChecker = useCallback(
    (permissionName: PermissionApiName) => {
      if (!permissionName) {
        return false;
      }
      const index = userInfo?.all_permissions?.findIndex(
        (x: any) => x?.name === permissionName
      );

      return !isNil(index) && index > -1;
    },
    [userInfo]
  );

  const userPermissions = useMemo(() => userInfo?.all_permissions || [], [
    userInfo
  ]);

  const userPermissionsStringArray = useMemo(() => {
    const clonedArr = cloneDeep(userInfo?.all_permissions) || [];
    // let arrStr: PermissionApiName[] = [];
    let arrStr: any = [];
    const mapped = clonedArr?.map((x) => x.name);
    arrStr = mapped?.filter((y) => y);
    return arrStr;
  }, [userInfo]);

  const canViewInventoryTotal = useMemo(
    () => permissionChecker('view_inventory_total'),
    [permissionChecker]
  );
  const canEditPurchaseType = useMemo(
    () => permissionChecker('can_edit_purchase_type'),
    [permissionChecker]
  );

  const canViewCustomerQueueing = useMemo(
    () => permissionChecker('can_view_customer_queueing'),
    [permissionChecker]
  );

  const canViewSales = useMemo(() => permissionChecker('view_sales'), [
    permissionChecker
  ]);
  const canViewSalesBasicDashboard = useMemo(
    () => permissionChecker('can_view_sales_basic_dashboard'),
    [permissionChecker]
  );

  const canViewTaptap = useMemo(() => permissionChecker('can_view_taptap'), [
    permissionChecker
  ]);

  const canViewTaptapReport = useMemo(
    () => permissionChecker('can_view_taptap_report'),
    [permissionChecker]
  );

  const canDeleteSupplier = useMemo(
    () => permissionChecker('can_delete_supplier'),
    [permissionChecker]
  );

  const canEditSupplier = useMemo(
    () => permissionChecker('can_edit_supplier'),
    [permissionChecker]
  );

  const canViewSuppliers = useMemo(
    () => permissionChecker('can_view_suppliers'),
    [permissionChecker]
  );

  const canViewSupplierDetails = useMemo(
    () => permissionChecker('can_view_supplier_details'),
    [permissionChecker]
  );

  const canAddSuppliers = useMemo(
    () => permissionChecker('can_add_suppliers'),
    [permissionChecker]
  );

  const canEditOptions = useMemo(() => permissionChecker('can_edit_options'), [
    permissionChecker
  ]);

  const canReplaceTransactionSerial = useMemo(
    () => permissionChecker('transaction_replace_serial_no'),
    [permissionChecker]
  );

  const canEditPriceListPrice = useMemo(
    () => permissionChecker('edit_pricelist_price'),
    [permissionChecker]
  );

  const canManageUsers = useMemo(() => permissionChecker('manage_users'), [
    permissionChecker
  ]);

  const canManagePermissions = useMemo(
    () => permissionChecker('edit_permissions_roles'),
    [permissionChecker]
  );

  const canEditBranches = useMemo(
    () => permissionChecker('can_edit_branches'),
    [permissionChecker]
  );

  // listings related
  const canUpdateListing = useMemo(
    () => permissionChecker('can_update_listing'),
    [permissionChecker]
  );

  const canDeleteListing = useMemo(
    () => permissionChecker('can_delete_listing'),
    [permissionChecker]
  );

  // for purchaser role permissions
  const canSeeDealersPrice = useMemo(
    () => permissionChecker('can_see_dealers_price'),
    [permissionChecker]
  );

  const canSeeGrossPrice = useMemo(
    () => permissionChecker('can_see_gross_price'),
    [permissionChecker]
  );

  const canSeeMarginPercentage = useMemo(
    () => permissionChecker('can_see_margin_percentage'),
    [permissionChecker]
  );

  // For accounting / purchaser
  const canSeeAvailableInventory = useMemo(
    () => permissionChecker('can_see_available_inventory'),
    [permissionChecker]
  );

  const canViewStockTransfer = useMemo(
    () => permissionChecker('view_stock_transfer'),
    [permissionChecker]
  );

  const canReceiveTransferStocks = useMemo(
    () => permissionChecker('can_receive_transfer_stocks'),
    [permissionChecker]
  );

  const canViewStockTransferLogs = useMemo(
    () => permissionChecker('can_view_transfer_stocks_logs'),
    [permissionChecker]
  );

  // for marketing role permissions
  const canViewEcomBanners = useMemo(
    () => permissionChecker('view_ecomm_banners'),
    [permissionChecker]
  );
  const canEditEcomBanners = useMemo(
    () => permissionChecker('edit_ecomm_banners'),
    [permissionChecker]
  );

  const canViewEcomOrder = useMemo(
    () => permissionChecker('can_view_ecom_order'),
    [permissionChecker]
  );

  const canViewFlashDeals = useMemo(
    () => permissionChecker('can_see_flash_deals'),
    [permissionChecker]
  );

  const canAddFlashDeals = useMemo(
    () => permissionChecker('can_add_flash_deals'),
    [permissionChecker]
  );

  const canEditFlashDeals = useMemo(
    () => permissionChecker('can_edit_flash_deals'),
    [permissionChecker]
  );

  const canEditCategory = useMemo(
    () => permissionChecker('can_edit_category'),
    [permissionChecker]
  );

  const canDeleteCategory = useMemo(
    () => permissionChecker('can_delete_category'),
    [permissionChecker]
  );

  const canAddCategory = useMemo(() => permissionChecker('can_add_category'), [
    permissionChecker
  ]);

  const canSMSBlast = useMemo(() => permissionChecker('can_sms_blast'), [
    permissionChecker
  ]);

  //for pc package permission
  const canCreatePCPackage = useMemo(
    () => permissionChecker('can_create_pc_package'),
    [permissionChecker]
  );

  const canEditPCPackage = useMemo(
    () => permissionChecker('can_edit_pc_package'),
    [permissionChecker]
  );

  const canDeletePCPackage = useMemo(
    () => permissionChecker('can_delete_pc_package'),
    [permissionChecker]
  );

  const canViewPCPackageDetails = useMemo(
    () => permissionChecker('can_view_pc_package_details'),
    [permissionChecker]
  );

  const canViewPCPackage = useMemo(
    () => permissionChecker('can_view_pc_package'),
    [permissionChecker]
  );

  //for pcw account
  const canViewEcomUserAccount = useMemo(
    () => permissionChecker('view_ecom_user_account'),
    [permissionChecker]
  );

  const canEditEcomUserAccount = useMemo(
    () => permissionChecker('can_edit_ecom_user_account'),
    [permissionChecker]
  );

  //for pcw account addresses
  const canViewUserEcommAddress = useMemo(
    () => permissionChecker('can_view_user_address'),
    [permissionChecker]
  );

  const canViewUserEcommAddressDetail = useMemo(
    () => permissionChecker('can_view_user_address_details'),
    [permissionChecker]
  );

  const canDeleteUserEcommAddress = useMemo(
    () => permissionChecker('can_delete_user_address'),
    [permissionChecker]
  );
  //for minigame
  const canViewMiniGame = useMemo(() => permissionChecker('view_minigame'), [
    permissionChecker
  ]);

  //for code event generator
  const canViewCodeEvent = useMemo(() => permissionChecker('view_code_event'), [
    permissionChecker
  ]);

  const canGenerateCodeEvent = useMemo(
    () => permissionChecker('can_generate_code_event'),
    [permissionChecker]
  );

  const canViewKachiPoints = useMemo(
    () => permissionChecker('view_kachi_points'),
    [permissionChecker]
  );

  const featureToggleKachiPoints = useMemo(
    () => permissionChecker('feature_toggle_kachi_points'),
    [permissionChecker]
  );

  const canAddKachiPoints = useMemo(
    () => permissionChecker('can_add_kachi_points'),
    [permissionChecker]
  );

  const canForfeitKachiPoints = useMemo(
    () => permissionChecker('can_forfeit_kachi_points'),
    [permissionChecker]
  );

  const canShowHistoryKachiPoints = useMemo(
    () => permissionChecker('can_show_history_points'),
    [permissionChecker]
  );

  //======BRANCH PERMISSION =========
  const canViewBranchUsers = useMemo(
    () => permissionChecker('can_view_branch_users'),
    [permissionChecker]
  );

  const canAddBranchUsers = useMemo(
    () => permissionChecker('can_add_branch_users'),
    [permissionChecker]
  );

  const canDeleteBranchUsers = useMemo(
    () => permissionChecker('can_delete_branch_users'),
    [permissionChecker]
  );

  //for products recent out of stock item
  const viewRecentOutOfStockProducts = useMemo(
    () => permissionChecker('view_recent_out_of_stock'),
    [permissionChecker]
  );

  //======for pricelist v2 permission
  const viewPricelist = useMemo(() => permissionChecker('can_view_pricelist'), [
    permissionChecker
  ]);

  const canSeePriceListDealersPrice = useMemo(
    () => permissionChecker('can_see_pricelist_delears_price'),
    [permissionChecker]
  );

  const canSeePriceListGrossPrice = useMemo(
    () => permissionChecker('can_see_pricelist_gross_price'),
    [permissionChecker]
  );

  const canSeePriceListMarginPercentage = useMemo(
    () => permissionChecker('can_see_pricelist_margin_percentage'),
    [permissionChecker]
  );
  const canDownloadPriceListCSV = useMemo(
    () => permissionChecker('can_download_pricelist'),
    [permissionChecker]
  );

  const canUpdateRetailPrice = useMemo(
    () => permissionChecker('can_update_pricelist_retail_price'),
    [permissionChecker]
  );

  const canViewDecayingStocks = useMemo(
    () => permissionChecker('can_view_decaying_stocks'),
    [permissionChecker]
  );

  const canViewFastMovingProducts = useMemo(
    () => permissionChecker('can_view_fast_moving_products'),
    [permissionChecker]
  );

  const canViewEcommSavedBuilds = useMemo(
    () => permissionChecker('can_view_ecomm_saved_builds'),
    [permissionChecker]
  );

  const canUpdateEcommSavedBuilds = useMemo(
    () => permissionChecker('can_update_ecomm_saved_builds'),
    [permissionChecker]
  );

  const canDeleteEcommSavedBuilds = useMemo(
    () => permissionChecker('can_delete_ecomm_saved_builds'),
    [permissionChecker]
  );

  const canVoidTransaction = useMemo(
    () => permissionChecker('can_void_transaction'),
    [permissionChecker]
  );

  const canAddTransactionPayment = useMemo(
    () => permissionChecker('can_add_transaction_payment'),
    [permissionChecker]
  );

  const canAddTransactionPaymentType = useMemo(
    () => permissionChecker('can_add_transaction_payment_type'),
    [permissionChecker]
  );

  const canDeleteTransactionPayment = useMemo(
    () => permissionChecker('can_delete_transaction_payment'),
    [permissionChecker]
  );

  const canEditTransactionSRPPercentage = useMemo(
    () => permissionChecker('can_edit_transaction_srp_percentage'),
    [permissionChecker]
  );

  const canSRPToggleNegativeOnePercent = useMemo(
    () => permissionChecker('can_srp_toggle_transaction_negative_1_percent'),
    [permissionChecker]
  );

  const canSRPToggleElevenPercent = useMemo(
    () => permissionChecker('can_srp_toggle_transaction_11_percent'),
    [permissionChecker]
  );

  const canSRPToggleZeroPercent = useMemo(
    () => permissionChecker('can_srp_toggle_transaction_0_percent'),
    [permissionChecker]
  );

  const canSRPToggleTwelvePercent = useMemo(
    () => permissionChecker('can_srp_toggle_transaction_12_percent'),
    [permissionChecker]
  );

  const canSyncTransactionPaymentVoucher = useMemo(
    () => permissionChecker('can_sync_transaction_payment_voucher'),
    [permissionChecker]
  );

  const canGenerateEcommOrderPaymentSuccessLink = useMemo(
    () => permissionChecker('can_generate_ecomm_order_payment_success_link'),
    [permissionChecker]
  );

  const canUpdateProductDescription = useMemo(
    () => permissionChecker('can_update_product_description'),
    [permissionChecker]
  );

  const viewSalesDashboard = useMemo(
    () => permissionChecker('view_sales_dashboard'),
    [permissionChecker]
  );

  //===blog permission===
  const canViewBlog = useMemo(() => permissionChecker('can_view_blog'), [
    permissionChecker
  ]);

  const canUpdateBlog = useMemo(() => permissionChecker('can_update_blog'), [
    permissionChecker
  ]);

  const canDeleteBlog = useMemo(() => permissionChecker('can_delete_blog'), [
    permissionChecker
  ]);

  const canCreateBlog = useMemo(() => permissionChecker('can_create_blog'), [
    permissionChecker
  ]);
  const viewTransferListingStock = useMemo(
    () => permissionChecker('can_view_listing_transfer_stocks_list'),
    [permissionChecker]
  );

  const canCreateBlogCategory = useMemo(
    () => permissionChecker('can_create_blog_category'),
    [permissionChecker]
  );

  const canUpdateBlogCategory = useMemo(
    () => permissionChecker('can_update_blog_category'),
    [permissionChecker]
  );

  const canDeleteBlogCategory = useMemo(
    () => permissionChecker('can_delete_blog_category'),
    [permissionChecker]
  );

  const canViewBlogCategory = useMemo(
    () => permissionChecker('can_view_blog_category'),
    [permissionChecker]
  );

  //===INVENTORY AUDIT PERMISSION ===

  const canInventoryAudit = useMemo(
    () => permissionChecker('can_inventory_audit'),
    [permissionChecker]
  );

  const canViewAuditLogs = useMemo(
    () => permissionChecker('can_view_audit_logs'),
    [permissionChecker]
  );

  const canCreateInventoryLog = useMemo(
    () => permissionChecker('can_create_inventory_audit_log'),
    [permissionChecker]
  );

  const canSeeActualAvailableProductCount = useMemo(
    () => permissionChecker('can_see_actual_available_product_count'),
    [permissionChecker]
  );

  const canCreatePromotional = useMemo(
    () => permissionChecker('can_create_promotional'),
    [permissionChecker]
  );

  const canDeleteTaptapCustomer = useMemo(
    () => permissionChecker('can_delete_taptap_customer'),
    [permissionChecker]
  );

  const cannotSelectTaptapBranch = useMemo(
    () => permissionChecker('cannot_select_taptap_branch'),
    [permissionChecker]
  );

  const canUnlinkTransactionNo = useMemo(
    () => permissionChecker('can_unlink_taptap_internal_transaction_no'),
    [permissionChecker]
  );

  const canDownloadTaptap = useMemo(
    () => permissionChecker('can_download_taptap'),
    [permissionChecker]
  );

  const canDeleteUploadedPaymentImageTaptap = useMemo(
    () => permissionChecker('can_delete_image_taptap'),
    [permissionChecker]
  );

  const canViewTaptapSalesDashboard = useMemo(
    () => permissionChecker('can_view_taptap_sales_dashboard'),
    [permissionChecker]
  );

  const canViewTaptapCashierDashboard = useMemo(
    () => permissionChecker('can_view_taptap_cashier_dashboard'),
    [permissionChecker]
  );

  const canViewTaptapAccountingDashboard = useMemo(
    () => permissionChecker('can_view_taptap_accounting_dashboard'),
    [permissionChecker]
  );

  const canViewAfterCareDashboard = useMemo(
    () => permissionChecker('can_view_after_care_dashboard'),
    [permissionChecker]
  );

  const canViewTaptapBuildDashboard = useMemo(
    () => permissionChecker('can_view_taptap_build_dashboard'),
    [permissionChecker]
  );

  const canViewTaptapWarehouseDashboard = useMemo(
    () => permissionChecker('can_view_taptap_warehouse_dashboard'),
    [permissionChecker]
  );

  const canViewAllTaptapDashboard = useMemo(
    () => permissionChecker('can_view_all_taptap_dashboard'),
    [permissionChecker]
  );

  const readOnlyTaptapAdminDashboard = useMemo(
    () => permissionChecker('read_only_taptap_admin_dashboard'),
    [permissionChecker]
  );

  const canCreateManufacturer = useMemo(
    () => permissionChecker('can_create_manufacturer'),
    [permissionChecker]
  );

  const canEditManufacturer = useMemo(
    () => permissionChecker('can_edit_manufacturer'),
    [permissionChecker]
  );

  // RMA
  const canUpdateRMA = useMemo(() => permissionChecker('can_update_rma'), [
    permissionChecker
  ]);
  const canViewRMA = useMemo(() => permissionChecker('can_view_rma'), [
    permissionChecker
  ]);
  const canCreateRMA = useMemo(() => permissionChecker('can_create_rma'), [
    permissionChecker
  ]);
  const canDeleteRMA = useMemo(() => permissionChecker('can_delete_rma'), [
    permissionChecker
  ]);

  // Voucher
  const canCreateVoucher = useMemo(
    () => permissionChecker('can_create_voucher'),
    [permissionChecker]
  );
  const canReadVoucher = useMemo(() => permissionChecker('can_read_voucher'), [
    permissionChecker
  ]);
  const canUpdateVoucher = useMemo(
    () => permissionChecker('can_update_voucher'),
    [permissionChecker]
  );
  const canDeleteVoucher = useMemo(
    () => permissionChecker('can_delete_voucher'),
    [permissionChecker]
  );

  // Sell Out
  const canViewSellOut = useMemo(() => permissionChecker('can_view_sell_out'), [
    permissionChecker
  ]);
  // Purchase Order
  const canCreatePurchaseOrder = useMemo(
    () => permissionChecker('can_create_po'),
    [permissionChecker]
  );

  const canViewPurchaseOrder = useMemo(() => permissionChecker('can_read_po'), [
    permissionChecker
  ]);

  const canViewPurchaseOrderReports = useMemo(
    () => permissionChecker('can_view_purchase_order_report'),
    [permissionChecker]
  );

  const canDownloadPurchaseOrderReports = useMemo(
    () => permissionChecker('can_download_purchase_order_report'),
    [permissionChecker]
  );

  const canUpdatePurchaseOrder = useMemo(
    () => permissionChecker('can_update_po'),
    [permissionChecker]
  );

  const canDeletePurchaseOrder = useMemo(
    () => permissionChecker('can_delete_po'),
    [permissionChecker]
  );

  const canViewAllPurchaseOrderStatus = useMemo(
    () => permissionChecker('can_view_all_po_status'),
    [permissionChecker]
  );

  // PC Gallery
  const canViewPCGallery = useMemo(
    () => permissionChecker('can_view_pc_gallery'),
    [permissionChecker]
  );
  const canViewPCGalleryDetails = useMemo(
    () => permissionChecker('can_view_pc_gallery_details'),
    [permissionChecker]
  );
  const canAddPCGallery = useMemo(
    () => permissionChecker('can_add_pc_gallery'),
    [permissionChecker]
  );
  const canEditPCGallery = useMemo(
    () => permissionChecker('can_edit_pc_gallery'),
    [permissionChecker]
  );

  const isNerfed = useMemo(() => permissionChecker('is_nerfed'), [
    permissionChecker
  ]);

  // Product-In
  const canViewProductInPO = useMemo(
    () => permissionChecker('can_view_product_in_po'),
    [permissionChecker]
  );

  const canCreateProductInPO = useMemo(
    () => permissionChecker('can_create_product_in_po'),
    [permissionChecker]
  );

  const canDownloadBranchQuestionaireCSV = useMemo(
    () => permissionChecker('can_download_branch_questionaire_csv'),
    [permissionChecker]
  );

  const canShowAllBranchColumnOnTapTap = useMemo(
    () => permissionChecker('can_show_all_branch_column_on_taptap'),
    [permissionChecker]
  );

  const canViewSalesPriceList = useMemo(
    () => permissionChecker('can_view_sales_pricelist'),
    [permissionChecker]
  );

  const canViewLiabilityChart = useMemo(
    () => permissionChecker('can_view_liability_chart'),
    [permissionChecker]
  );

  const canViewLiabilitySchedule = useMemo(
    () => permissionChecker('can_view_liability_schedule'),
    [permissionChecker]
  );

  const canViewPoCheque = useMemo(
    () => permissionChecker('can_view_po_cheque'),
    [permissionChecker]
  );

  const canViewCustomers = useMemo(
    () => permissionChecker('can_view_customers'),
    [permissionChecker]
  );

  const canViewQuotation = useMemo(
    () => permissionChecker('can_view_quotation'),
    [permissionChecker]
  );

  const canSaveTemplateQuotation = useMemo(
    () => permissionChecker('can_save_template_quotation'),
    [permissionChecker]
  );

  const canViewTransactions = useMemo(
    () => permissionChecker('can_view_transactions'),
    [permissionChecker]
  );

  const canViewTransactionsDetails = useMemo(
    () => permissionChecker('can_view_transaction_details'),
    [permissionChecker]
  );

  const canViewInventory = useMemo(
    () => permissionChecker('can_view_inventory'),
    [permissionChecker]
  );

  const canExtractNvidiaBuyIn = useMemo(
    () => permissionChecker('can_extract_nvidia_buy_in'),
    [permissionChecker]
  );

  const canViewProducts = useMemo(
    () => permissionChecker('can_view_products'),
    [permissionChecker]
  );

  const canViewProductDetails = useMemo(
    () => permissionChecker('can_view_product_details'),
    [permissionChecker]
  );

  const canAddProducts = useMemo(() => permissionChecker('can_add_products'), [
    permissionChecker
  ]);

  const canAddProductListing = useMemo(
    () => permissionChecker('can_add_product_listing'),
    [permissionChecker]
  );

  const canEditProductInformation = useMemo(
    () => permissionChecker('can_edit_product_information'),
    [permissionChecker]
  );

  const canViewCategories = useMemo(
    () => permissionChecker('can_view_categories'),
    [permissionChecker]
  );

  const canViewManufacturers = useMemo(
    () => permissionChecker('can_view_manufacturers'),
    [permissionChecker]
  );

  const canViewGames = useMemo(() => permissionChecker('can_view_games'), [
    permissionChecker
  ]);

  const canExtractSalesPayments = useMemo(
    () => permissionChecker('can_extract_sales_payments'),
    [permissionChecker]
  );

  const canViewEvent = useMemo(() => permissionChecker('can_view_event'), [
    permissionChecker
  ]);

  const canCreateEvent = useMemo(() => permissionChecker('can_create_event'), [
    permissionChecker
  ]);

  const canUpdateEvent = useMemo(() => permissionChecker('can_update_event'), [
    permissionChecker
  ]);

  const canDeleteEvent = useMemo(() => permissionChecker('can_delete_event'), [
    permissionChecker
  ]);

  const canConfirmCashierTapTapPayment = useMemo(
    () => permissionChecker('can_confirm_taptap_payment_cashier'),
    [permissionChecker]
  );

  const canCreate2307Form = useMemo(
    () => permissionChecker('can_create_2307_form'),
    [permissionChecker]
  );

  const canViewTaxCertificate = useMemo(
    () => permissionChecker('can_view_tax_certificate'),
    [permissionChecker]
  );

  // PC Bundles
  const canViewPCBundle = useMemo(
    () => permissionChecker('can_view_pc_bundle'),
    [permissionChecker]
  );

  const canViewPCBundleDetails = useMemo(
    () => permissionChecker('can_view_pc_bundle_details'),
    [permissionChecker]
  );

  const canEditPCBundle = useMemo(
    () => permissionChecker('can_edit_pc_bundle'),
    [permissionChecker]
  );

  const canCreatePCBundle = useMemo(
    () => permissionChecker('can_create_pc_bundle'),
    [permissionChecker]
  );

  const canViewPCBundleComponents = useMemo(
    () => permissionChecker('can_view_pc_bundle_components'),
    [permissionChecker]
  );

  const canCreatePCBundleComponents = useMemo(
    () => permissionChecker('can_create_pc_bundle_components'),
    [permissionChecker]
  );

  // Emailer
  const canUpdateEmailer = useMemo(
    () => permissionChecker('can_update_emailer'),
    [permissionChecker]
  );

  const canCreateEmailer = useMemo(
    () => permissionChecker('can_create_emailer'),
    [permissionChecker]
  );

  const canViewEmailer = useMemo(() => permissionChecker('can_view_emailer'), [
    permissionChecker
  ]);

  const canViewCustomerPayment = useMemo(
    () => permissionChecker('can_view_customer_payment'),
    [permissionChecker]
  );

  /**
   * Allow to set listing status. to purchase, defective and loss
   * special permission for owner only
   */
  const canUpdateListingStatus = useMemo(
    () => permissionChecker('can_update_listings_status'),
    [permissionChecker]
  );

  //CRM sales permissions
  const canViewCrmSales = useMemo(
    () => permissionChecker('can_view_crm_sales'),
    [permissionChecker]
  );

  const canByPassCrmTicket = useMemo(
    () => permissionChecker('can_bypass_crm_ticket'),
    [permissionChecker]
  );

  return {
    userPermissions,
    userPermissionsStringArray,
    permissionChecker,
    canViewSales,
    canViewSalesBasicDashboard,
    canViewCustomers,
    canViewTaptap,
    canViewTaptapReport,
    canManageUsers,
    canManagePermissions,
    canViewCustomerQueueing,
    canEditPriceListPrice,
    canViewInventoryTotal,
    canEditPurchaseType,
    canEditCategory,
    canAddCategory,
    canDeleteCategory,
    canEditBranches,
    canEditOptions,
    canEditSupplier,
    canDeleteSupplier,
    canViewEcomOrder,
    canViewSuppliers,
    canViewSupplierDetails,
    canAddSuppliers,

    // For listings
    canUpdateListing,
    canDeleteListing,

    // For purchaser role
    canSeeDealersPrice,
    canSeeGrossPrice,
    canSeeMarginPercentage,
    canSeeAvailableInventory,
    canViewStockTransfer,
    canReceiveTransferStocks,
    canViewStockTransferLogs,

    // For Marketing Role
    canViewEcomBanners,
    canEditEcomBanners,
    canAddFlashDeals,
    canEditFlashDeals,
    canViewFlashDeals,
    canCreatePCPackage,
    canEditPCPackage,
    canDeletePCPackage,
    canViewPCPackage,
    canViewPCPackageDetails,
    canSMSBlast,

    // For pcw user account
    canEditEcomUserAccount,
    canViewEcomUserAccount,

    // For pcw ecomm user address
    canViewUserEcommAddress,
    canViewUserEcommAddressDetail,
    canDeleteUserEcommAddress,

    // For pcw ecomm user builds
    canViewEcommSavedBuilds,
    canDeleteEcommSavedBuilds,
    canUpdateEcommSavedBuilds,

    //for minigame
    canViewMiniGame,

    //for code event
    canViewCodeEvent,
    canGenerateCodeEvent,

    //for kachi points
    canViewKachiPoints,
    featureToggleKachiPoints,
    canAddKachiPoints,
    canForfeitKachiPoints,
    canShowHistoryKachiPoints,

    //for branch views
    canViewBranchUsers,
    canAddBranchUsers,
    canDeleteBranchUsers,

    //for product recent out of stocks
    viewRecentOutOfStockProducts,

    //for pricelist v2
    viewPricelist,
    canSeePriceListDealersPrice,
    canSeePriceListGrossPrice,
    canSeePriceListMarginPercentage,
    canDownloadPriceListCSV,
    canUpdateRetailPrice,

    //decaying stocks
    canViewDecayingStocks,

    //fast moving products
    canViewFastMovingProducts,

    //can update product description
    canUpdateProductDescription,

    // transactions
    canReplaceTransactionSerial,
    canVoidTransaction,
    canDeleteTransactionPayment,
    canAddTransactionPayment,
    canAddTransactionPaymentType,
    canGenerateEcommOrderPaymentSuccessLink,
    canSRPToggleTwelvePercent,
    canSRPToggleZeroPercent,
    canSRPToggleNegativeOnePercent,
    canSRPToggleElevenPercent,
    canEditTransactionSRPPercentage,
    canSyncTransactionPaymentVoucher,

    // dashboard
    viewSalesDashboard,

    //blog
    canViewBlog,
    canUpdateBlog,
    canDeleteBlog,
    canCreateBlog,

    //transfer stock listing
    viewTransferListingStock,

    //blog category
    canCreateBlogCategory,
    canUpdateBlogCategory,
    canDeleteBlogCategory,
    canViewBlogCategory,

    //inventory audit
    canInventoryAudit,
    canViewAuditLogs,
    canCreateInventoryLog,
    canSeeActualAvailableProductCount,

    //promotional
    canCreatePromotional,

    // taptap
    canDeleteTaptapCustomer,
    cannotSelectTaptapBranch,
    canUnlinkTransactionNo,
    canDownloadTaptap,
    canShowAllBranchColumnOnTapTap,
    canConfirmCashierTapTapPayment,
    canDeleteUploadedPaymentImageTaptap,

    //TAPTAP V2 DASHBOARD ACCESS
    canViewTaptapSalesDashboard,
    canViewTaptapCashierDashboard,
    canViewTaptapAccountingDashboard,
    canViewAfterCareDashboard,
    canViewTaptapBuildDashboard,
    canViewTaptapWarehouseDashboard,
    canViewAllTaptapDashboard,
    readOnlyTaptapAdminDashboard,

    // manufacturer
    canCreateManufacturer,
    canEditManufacturer,

    // RMA
    canUpdateRMA,
    canViewRMA,
    canCreateRMA,
    canDeleteRMA,

    // Voucher
    canCreateVoucher,
    canUpdateVoucher,
    canDeleteVoucher,
    canReadVoucher,

    // Sell Out
    canViewSellOut,

    // Purchase Order
    canCreatePurchaseOrder,
    canViewPurchaseOrder,
    canViewPurchaseOrderReports,
    canDownloadPurchaseOrderReports,
    canUpdatePurchaseOrder,
    canDeletePurchaseOrder,
    canViewAllPurchaseOrderStatus,

    // Product In
    canViewProductInPO,
    canCreateProductInPO,

    // PC Gallery
    canViewPCGallery,
    canViewPCGalleryDetails,
    canAddPCGallery,
    canEditPCGallery,

    isNerfed,

    canDownloadBranchQuestionaireCSV,
    canViewSalesPriceList,

    // Liability modules
    canViewLiabilityChart,
    canViewLiabilitySchedule,
    canViewPoCheque,
    canViewQuotation,
    canSaveTemplateQuotation,
    canViewTransactions,
    canViewTransactionsDetails,
    canViewInventory,
    canExtractNvidiaBuyIn,
    canViewProducts,
    canAddProducts,
    canViewProductDetails,
    canAddProductListing,
    canEditProductInformation,
    canViewCategories,
    canViewManufacturers,
    canViewGames,
    canExtractSalesPayments,

    //Event
    canViewEvent,
    canCreateEvent,
    canUpdateEvent,
    canDeleteEvent,

    // Tax Certification 2307
    canCreate2307Form,
    canViewTaxCertificate,
    // PC Bundles
    canViewPCBundle,
    canViewPCBundleDetails,
    canCreatePCBundle,
    canEditPCBundle,
    canViewPCBundleComponents,
    canCreatePCBundleComponents,

    // Email Marketing
    canUpdateEmailer,
    canCreateEmailer,
    canViewEmailer,

    //customer payment
    canViewCustomerPayment,

    canUpdateListingStatus,

    //CRM Sales
    canViewCrmSales,
    canByPassCrmTicket
  };
};
